/** Общие блоки P&L: KPI-карточки и таблица P&L (группы → статьи) — вкладка «Сводка» и карточка
 *  сущности (боковая панель). */
import { ChartLine, ChevronDown, ChevronRight } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";

import type { OzFilters, OzSummary, SummaryGroup, SummaryRow } from "@/api/ozonAnalytics";
import { cn } from "@/lib/utils";
import { grpKind, TONE_CLASS, type Kind } from "./calc";
import {
  COGS_GROUP_LABEL,
  COGS_HINT,
  cogsNullTitle,
  cogsTotal,
  EXPENSES_LABEL,
  expensesHint,
  NET_MARGIN_LABEL,
  pricesAt,
} from "./cogs";
import {
  DELTA_HEAD_TITLE,
  pnlColumnsCh,
  pnlCompactCells,
  pnlLabelCh,
  pnlOptCells,
  pnlReferenceCells,
} from "./compact";
import { ItemTrendDialog, type ItemTrendTarget } from "./ItemTrendDialog";
import type { Kpi } from "./kpi";
import { useMp } from "./mp";
import { CompactTd, Unreliable } from "./shared";
import { thClass } from "./ui";

// ─────────────── KPI ───────────────

/** KPI-карточки: значение, «было», Δ. `compact` — карточка сущности: 3 колонки, мельче,
 *  пояснение — подсказкой на названии, а не строкой. Не `compact` («Сводка», 6 карточек) — от
 *  1280 px (`xl`) все шесть в один ряд (значение — `text-lg`, чтобы «17 242 429 ₽» влезало). */
export function KpiGrid({
  kpis,
  warning,
  compact = false,
}: {
  kpis: Kpi[];
  warning: string | null;
  compact?: boolean;
}): React.ReactElement {
  return (
    <div
      data-testid="kpi-grid"
      className={cn(
        "grid",
        compact
          ? "grid-cols-2 gap-2 sm:grid-cols-3"
          : "gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 xl:gap-2",
      )}
    >
      {kpis.map((k) => (
        <div
          key={k.title}
          className={cn("rounded-md border bg-card", compact ? "p-2" : "p-3")}
          data-testid="kpi"
        >
          <div
            className="text-xs text-muted-foreground"
            title={compact ? k.hint : undefined}
          >
            {k.title}
          </div>
          <div
            className={cn(
              "mt-1 font-semibold tabular-nums",
              compact ? "text-base" : "text-xl xl:text-lg",
            )}
            title={k.curTitle ?? undefined}
          >
            {k.cur}
          </div>
          {k.note && (
            <div className="mt-0.5 text-xs leading-tight text-amber-700 dark:text-amber-400" data-testid="kpi-note">
              {k.note}
            </div>
          )}
          {k.was !== null && (
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 text-xs tabular-nums">
              <span className="text-muted-foreground" title={k.wasTitle ?? undefined}>
                было {k.was}
              </span>
              <span className={TONE_CLASS[k.tone]}>
                {k.deltaAbs}
                {k.deltaRel !== null && ` · ${k.deltaRel}`}
                <Unreliable warning={warning} />
              </span>
            </div>
          )}
          {!compact && (
            <div className="mt-1 text-[11px] leading-tight text-muted-foreground">
              {k.hint}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─────────────── P&L ───────────────

/** Иконка графика строки P&L: видна при наведении на строку и при фокусе клавиатурой;
 *  клик не раскрывает группу. */
function ChartButton({
  label,
  onOpen,
}: {
  label: string;
  onOpen: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      aria-label={`График: ${label}`}
      title="График: текущий период против сравнения"
      className="ml-auto shrink-0 rounded p-0.5 text-muted-foreground opacity-0 hover:bg-accent hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
    >
      <ChartLine className="size-3.5" />
    </button>
  );
}

function PnlCells({
  cur,
  cmp,
  revCur,
  revCmp,
  hasCmp,
  kind,
}: {
  cur: number;
  cmp: number | null;
  revCur: number;
  revCmp: number | null;
  hasCmp: boolean;
  /** выручка и маржа — доход, удержания и расходы — расход (см. `calc.ts`) */
  kind: Kind;
}): React.ReactElement {
  return (
    <>
      {pnlCompactCells(cur, cmp, revCur, revCmp, hasCmp, kind).map((c, i) => (
        <CompactTd
          key={i}
          cell={c}
          className={cn(i === 1 && "text-muted-foreground")}
        />
      ))}
    </>
  );
}

/** Ячейки строк себестоимости, «Расходов» и «Маржи»: null — «—» с подсказкой почему. */
function OptCells({
  cur,
  cmp,
  revCur,
  revCmp,
  hasCmp,
  kind,
  nullTitle,
  nullTitleCmp,
}: {
  cur: number | null;
  cmp: number | null;
  revCur: number;
  revCmp: number | null;
  hasCmp: boolean;
  kind: Kind;
  nullTitle: string;
  nullTitleCmp: string;
}): React.ReactElement {
  return (
    <>
      {pnlOptCells(cur, cmp, revCur, revCmp, hasCmp, kind, nullTitle, nullTitleCmp).map(
        (c, i) => (
          <CompactTd key={i} cell={c} className={cn(i === 1 && "text-muted-foreground")} />
        ),
      )}
    </>
  );
}

/** Строка блока «Справочно»: суммы и Δ, без долей выручки и «Δ п.п.». */
function ReferenceCells({
  cur,
  cmp,
  hasCmp,
}: {
  cur: number;
  cmp: number | null;
  hasCmp: boolean;
}): React.ReactElement {
  return (
    <>
      {pnlReferenceCells(cur, cmp, hasCmp).map((c, i) => (
        <CompactTd key={i} cell={c} className={cn(i === 1 && "text-muted-foreground")} />
      ))}
    </>
  );
}

/** Таблица P&L по схеме «Выручка − Расходы = Маржа» (решение владельца 22.10.2026): группы →
 *  статьи (раскрытие), «Сейчас / Было / Δ п.п. / Δ», под группами удержаний — «Себестоимость»
 *  (производство + склад одним числом), «Расходы, всего» (`expenses` = все удержания + себестоимость)
 *  и «Маржа» (`net_margin`). Без себестоимости (период раньше `cogs_from`, цен нет) эти три строки —
 *  «—» с подсказкой «себестоимость считается с …», а не удержания / маржа до себестоимости; строк
 *  группы `cogs` в `rows`/`groups` нет — бэкенд держит её вне P&L площадки.
 *  Справочные группы (`reference`, WB `spp`) в расходы, доли, Δ п.п. и маржу не входят:
 *  отдельным блоком «Справочно» под маржой — группа и все её статьи, суммы и Δ без долей.
 *  `charts=false` — без иконок графика строк (карточка сущности).
 *  `margin=false` — поставка: выручки и себестоимости нет, маржи нет — итог одной строкой
 *  «Удержания <площадка>, всего» (это не «Расходы» схемы маржи).
 *  `compact` — боковая панель: таблица во всю ширину панели, числовые колонки — те же ширины в ch,
 *  колонка статей — остаток (длинные подписи обрезаются, полностью — в подсказке); без
 *  горизонтальной прокрутки. Без `compact` (вкладка «Сводка») — по ширине содержимого. */
export function PnlTable({
  summary,
  filters,
  warning = null,
  charts = true,
  compact = false,
  margin = true,
}: {
  summary: OzSummary;
  filters: OzFilters;
  warning?: string | null;
  charts?: boolean;
  compact?: boolean;
  margin?: boolean;
}): React.ReactElement {
  const mp = useMp();
  const hasCmp = summary.totals.cmp !== null;
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [chart, setChart] = useState<ItemTrendTarget | null>(null);
  const itemsByGrp = useMemo(() => {
    const m = new Map<string, SummaryRow[]>();
    for (const r of summary.rows) {
      const list = m.get(r.grp) ?? [];
      list.push(r);
      m.set(r.grp, list);
    }
    return m;
  }, [summary.rows]);
  const revCur = summary.totals.cur.revenue;
  const revCmp = summary.totals.cmp?.revenue ?? null;
  const toggle = (g: string): void =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  const closeChart = useCallback(() => setChart(null), []);
  // справочные группы — не P&L: отдельным блоком под маржой (итоги бэкенда их не содержат)
  const pnlGroups = summary.groups.filter((g) => !g.reference);
  const refGroups = summary.groups.filter((g) => g.reference);
  const allOpen = pnlGroups.every((g) => open.has(g.grp));
  const labelCh = useMemo(
    () => pnlLabelCh(summary.groups, summary.rows),
    [summary],
  );

  const groupRow = (g: SummaryGroup): React.ReactNode => {
    const isOpen = open.has(g.grp);
    const items = itemsByGrp.get(g.grp) ?? [];
    // окно графика: срез группы или статьи, внутри — переключение между ними и ₽ / % выручки
    const chartOf = (item: string | null): ItemTrendTarget => ({
      grp: g.grp,
      grpLabel: g.label,
      item,
      cur: g.cur,
      cmp: g.cmp,
      items: items.map((it) => ({ item: it.item, cur: it.cur, cmp: it.cmp })),
      revCur,
      revCmp,
      mart: summary.mart,
    });
    return (
      <React.Fragment key={g.grp}>
        <tr
          className="group cursor-pointer border-t font-medium hover:bg-accent/40"
          onClick={() => toggle(g.grp)}
          aria-expanded={isOpen}
        >
          <td className="px-2 py-1">
            <span className="flex min-w-0 items-center gap-1">
              {isOpen ? (
                <ChevronDown className="size-4 shrink-0" />
              ) : (
                <ChevronRight className="size-4 shrink-0" />
              )}
              <span className="truncate" title={g.label}>
                {g.label}
              </span>
              <span className="text-xs font-normal text-muted-foreground">
                {items.length}
              </span>
              {charts && (
                <ChartButton
                  label={g.label}
                  onOpen={() => setChart(chartOf(null))}
                />
              )}
            </span>
          </td>
          <PnlCells
            cur={g.cur}
            cmp={g.cmp}
            revCur={revCur}
            revCmp={revCmp}
            hasCmp={hasCmp}
            kind={grpKind(g.grp)}
          />
        </tr>
        {isOpen &&
          items.map((it) => (
            <tr
              key={`${g.grp}|${it.item}`}
              className="group border-t border-border/40 text-[13px] hover:bg-accent/30"
            >
              <td className="py-1 pl-8 pr-2 text-muted-foreground">
                <span className="flex min-w-0 items-center gap-1">
                  <span className="truncate" title={it.item}>
                    {it.item}
                  </span>
                  {charts && (
                    <ChartButton
                      label={it.item}
                      onOpen={() => setChart(chartOf(it.item))}
                    />
                  )}
                </span>
              </td>
              <PnlCells
                cur={it.cur}
                cmp={it.cmp}
                revCur={revCur}
                revCmp={revCmp}
                hasCmp={hasCmp}
                kind={grpKind(g.grp)}
              />
            </tr>
          ))}
      </React.Fragment>
    );
  };

  const c = summary.totals.cur;
  const p = summary.totals.cmp;
  const numCols = pnlColumnsCh(hasCmp);
  const nCur = cogsNullTitle(summary.cogs, mp.label, "cur");
  const nCmp = cogsNullTitle(summary.cogs, mp.label, "cmp");
  const cogsLine = (
    label: string,
    cur: number | null,
    cmp: number | null,
    kind: Kind,
  ): React.ReactNode => (
    <OptCells
      cur={cur}
      cmp={cmp}
      revCur={revCur}
      revCmp={revCmp}
      hasCmp={hasCmp}
      kind={kind}
      nullTitle={`${label}: ${nCur}`}
      nullTitleCmp={`${label}: ${nCmp}`}
    />
  );
  const prices = pricesAt(summary.cogs);
  return (
    <div
      className={cn(
        "max-w-full overflow-x-auto rounded-md border bg-card",
        compact ? "w-full" : "w-fit",
      )}
    >
      {/* ширины колонок заданы заранее (см. compact.ts) — не тянутся по экрану и не прыгают при раскрытии */}
      <table
        className={cn("table-fixed text-xs", compact && "w-full")}
        style={
          compact ? undefined : { width: `${labelCh + numCols.reduce((a, b) => a + b, 0)}ch` }
        }
        data-testid="pnl-table"
      >
        <colgroup>
          <col style={compact ? undefined : { width: `${labelCh}ch` }} />
          {numCols.map((w, i) => (
            <col key={i} style={{ width: `${w}ch` }} />
          ))}
        </colgroup>
        <thead className="bg-muted/40 text-[11px]">
          <tr>
            <th className={cn(thClass, "truncate text-left")}>
              <button
                type="button"
                className="normal-case text-primary hover:underline"
                onClick={() =>
                  setOpen(
                    allOpen
                      ? new Set()
                      : new Set(pnlGroups.map((g) => g.grp)),
                  )
                }
              >
                {allOpen ? "Свернуть все" : "Раскрыть все статьи"}
              </button>
            </th>
            <th className={thClass}>Сейчас</th>
            {hasCmp && (
              <>
                <th className={thClass}>Было</th>
                <th className={thClass} title="Δ доли выручки, п.п.">
                  Δ п.п.
                </th>
                <th className={thClass} title={DELTA_HEAD_TITLE}>
                  Δ
                  <Unreliable warning={warning} />
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {pnlGroups.map(groupRow)}
          {!margin && (
            <tr className="border-t-2 font-medium" data-testid="pnl-deductions">
              <td className="truncate px-2 py-1" title={`Удержания ${mp.label}, всего`}>
                Удержания {mp.label}, всего
              </td>
              <PnlCells
                cur={c.deductions}
                cmp={p?.deductions ?? null}
                revCur={revCur}
                revCmp={revCmp}
                hasCmp={hasCmp}
                kind="expense"
              />
            </tr>
          )}
          {margin && (
            <>
              <tr className="border-t font-medium" data-testid="pnl-cogs">
                <td
                  className="truncate px-2 py-1"
                  title={`${COGS_GROUP_LABEL}: ${COGS_HINT}${prices ? ` · ${prices}` : ""}`}
                >
                  {COGS_GROUP_LABEL}
                </td>
                {cogsLine(
                  COGS_GROUP_LABEL,
                  cogsTotal(c.cogs_goods, c.cogs_wh),
                  p ? cogsTotal(p.cogs_goods, p.cogs_wh) : null,
                  "expense",
                )}
              </tr>
              <tr className="border-t-2 font-medium" data-testid="pnl-expenses">
                <td
                  className="truncate px-2 py-1"
                  title={`${EXPENSES_LABEL}, всего: ${expensesHint(mp.label)}`}
                >
                  {EXPENSES_LABEL}, всего
                </td>
                {cogsLine(EXPENSES_LABEL, c.expenses ?? null, p?.expenses ?? null, "expense")}
              </tr>
              <tr className="border-t bg-muted/30 font-semibold" data-testid="pnl-net-margin">
                <td className="truncate px-2 py-1" title="Выручка − расходы">
                  {NET_MARGIN_LABEL}
                </td>
                {cogsLine(NET_MARGIN_LABEL, c.net_margin ?? null, p?.net_margin ?? null, "income")}
              </tr>
            </>
          )}
        </tbody>
        {refGroups.length > 0 && (
          <tbody data-testid="pnl-reference">
            <tr className="border-t-2">
              <td
                colSpan={1 + numCols.length}
                className="px-2 pb-0.5 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
              >
                Справочно
              </td>
            </tr>
            {refGroups.map((g) => (
              <React.Fragment key={g.grp}>
                <tr className="border-t text-muted-foreground" data-testid="pnl-reference-group">
                  <td className="truncate px-2 py-1 font-medium" title={g.label}>
                    {g.label}
                  </td>
                  <ReferenceCells cur={g.cur} cmp={g.cmp} hasCmp={hasCmp} />
                </tr>
                {(itemsByGrp.get(g.grp) ?? []).map((it) => (
                  <tr
                    key={`${g.grp}|${it.item}`}
                    className="border-t border-border/40 text-[13px] text-muted-foreground"
                    data-testid="pnl-reference-item"
                  >
                    <td className="truncate py-1 pl-8 pr-2" title={it.item}>
                      {it.item}
                    </td>
                    <ReferenceCells
                      cur={it.cur}
                      cmp={it.cmp}
                      hasCmp={hasCmp}
                    />
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        )}
      </table>
      {chart && (
        <ItemTrendDialog
          filters={filters}
          target={chart}
          warning={warning}
          onClose={closeChart}
        />
      )}
    </div>
  );
}
